<template>
  <b-modal
    :id="id"
    no-close-on-backdrop
    no-enforce-focus
    no-close-on-esc
    size="lg"
    @shown="onShown"
    @hidden="onHidden"
  >
    <template #modal-title>
      {{ trans(!page ? 'new_page' : 'page') }}
      <span v-if="!!page">: {{ page.name }}</span>
      <small v-if="!!page" class="text-muted ml-2">
        #{{ page.id }}
      </small>
      <small v-if="!page" class="ml-4">
        <b-button variant="outline-primary" size="sm" v-b-modal.create-page-from-sample-modal>
          {{ trans('samples') }}
        </b-button>
        <create-page-from-sample-modal
          :organization="organization"
          :after="after"
          :parent-page-id="parentPageId"
          :is-staff="isStaff"
        ></create-page-from-sample-modal>
      </small>
    </template>

    <b-row>
      <b-col>
        <b-form-group :label="trans('title')">
          <b-form-input v-model="name" :state="!name || !name.trim() ? false : null" :disabled="busy" />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('permission')">
          <b-form-select v-model="permission" :options="pagePermissionsOptions" :disabled="busy || page && !page.can_change_permission" />
        </b-form-group>
      </b-col>
    </b-row>
    
    <b-form-group v-if="organization.has_client_tabs && permission == 'client'" :label="trans('page_allowed_client_tabs')">
      <treeselect
        :options="allowedClientTabOptions"
        v-model="allowed_client_tab_ids"
        multiple
        clearable
        :disabled="busy"
      ></treeselect>
      <b-form-text>
        {{ trans('page_allowed_client_tabs_about') }}
      </b-form-text>
    </b-form-group>

    <b-form-group v-if="permission == 'admin'" :label="trans('page_hide_admin_permissions')">
      <treeselect
        :options="hideAdminPermissionOptions"
        v-model="hide_admin_permissions"
        multiple
        clearable
        :disabled="busy"
      ></treeselect>
      <b-form-text>
        {{ trans('page_hide_admin_permissions_about') }}
      </b-form-text>
    </b-form-group>

    <b-form-group v-if="permission == 'member'" :label="trans('page_allowed_memberships')">
      <model-treeselect
        :organization="organization"
        :url="`/organization/${organization.id}/membership/list?per_page=200`"
        v-model="membership_ids"
        :multiple="true"
        :placeholder="trans('memberships')"
        :disabled="busy"
        :clearable="false"
      ></model-treeselect>
      <b-form-text>
        {{ trans('page_allowed_memberships_about') }}
      </b-form-text>
    </b-form-group>

    <b-form-group v-if="permission == 'list'" :label="trans('page_allowed_list')">
      <list-dropdown
        :organization="organization"
        v-model="recipient_list_id"
        nullable
        size="sm"
        :disabled="busy"
      ></list-dropdown>
      <b-form-text>
        {{ trans('page_allowed_list_about') }}
      </b-form-text>
      <b-form-text v-if="!recipient_list_id">
        <span class="text-danger">
          {{ trans('required') }}
        </span>
      </b-form-text>
    </b-form-group>

    <b-form-checkbox switch v-model="force_locale" :disabled="busy" class="mb-2" v-if="organization.is_canadian">
      {{ trans('force_locale') }}
    </b-form-checkbox>

    <b-row v-if="force_locale">
      <b-col>
        <b-form-group :label="trans('language')">
          <b-form-select
            v-model="locale"
            :options="localeOptions"
            :disabled="busy"
          ></b-form-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="trans('translation_page')">
          <treeselect
            :options="translatedPages"
            v-model="translated_page_id"
            clearable
            :disabled="busy"
            :placeholder="trans('select')"
            @input="reload = true"
          ></treeselect>
        </b-form-group>
        <b-form-group label="Help page slug" v-if="organization.id == 2754 || organization.id == 1093">
          <b-form-input v-model="help_slug" :disabled="busy" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="!page || !page.is_built_in" class="mt-4">
      <b-col>
        <b-form-group :label="trans('external_page')">
          <b-form-checkbox switch v-model="has_external_url" :disabled="busy">
            {{ trans('external_page_desc') }}
          </b-form-checkbox>
        </b-form-group>
      </b-col>
      <b-col v-if="has_external_url">
        <b-form-group class="mt-4">
            <b-form-input v-model="external_url" :placeholder="trans('external_page_url')"  :disabled="busy"/>
            <small v-if="!isUrlValid" class="form-text text-danger text-right">
                {{ trans('invalid_url') }}
            </small>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group :label="trans('page_passcode')">
          <b-form-checkbox switch v-model="passcode_required" :disabled="busy">
            {{ trans('page_passcode_about') }}
          </b-form-checkbox>
        </b-form-group>
      </b-col>
      <b-col v-if="passcode_required">
        <b-form-group>
          <label>&nbsp;</label>
          <b-input-group>
            <b-form-input v-model="passcode" :placeholder="trans('passcode')" :state="!passcode || !passcode.trim() ? false : null" :disabled="busy" />
            <b-input-group-append>
              <b-button variant="outline-primary" v-clipboard:copy="passcode" :disabled="busy" :title="trans('copy')" v-b-tooltip.hover class="px-2">
                <i class="fa fa-fw fa-copy"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group v-if="!!page && page.url" label="URL">
      <b-input-group>
        <b-form-input :value="page.url" readonly />
        <b-input-group-append>
          <b-button variant="outline-primary" v-clipboard:copy="page.url" :title="trans('copy')" v-b-tooltip.hover class="px-2">
            <i class="fa fa-fw fa-copy"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <b-form-group :label="trans('navigation_menu')">
      <b-form-checkbox switch v-model="hide_navigation_menu" :disabled="busy">
        {{ trans('hide_navigation_menu') }}
      </b-form-checkbox>
      <b-form-text v-if="hide_navigation_menu">
        {{ trans('hide_navigation_menu_about') }}
      </b-form-text>
    </b-form-group>

    <hr/>
    <div class="my-2">
      <p class="mb-1">
        <span v-if="passcode_required" class="mr-2">
          <i class="fa fa-fw fa-lock text-muted"></i>
        </span>
        <b-badge v-if="permission == 'hidden'" variant="secondary" class="mr-2">
          {{ trans('hidden') }}
        </b-badge>
        <b-badge v-if="permission == 'client'" variant="secondary" class="mr-2">
          {{ trans('client') }}
        </b-badge>
        <b-badge v-if="permission == 'admin'" variant="danger" class="mr-2">
          {{ trans('admin') }}
        </b-badge>
        {{ trans('page_permission_' + permission + '_about') }}
      </p>
      <p v-if="passcode_required" class="mb-1">
        {{ trans('page_permission_passcode_about') }}
      </p>
      <p v-if="passcode_required" class="mb-1">
        {{ trans('page_permission_passcode_admin_note') }}
      </p>
    </div>

    <div v-if="organization.can_publish" class="mt-4">
      <hr/>
      <p><strong>{{ trans('published') }}</strong></p>
      <b-row>
        <b-col>
          <b-form-group :label="trans('published_to_partner_about')">
            <b-form-checkbox switch v-model="published" :disabled="busy">
              {{ published ? trans('yes') : trans('no') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group :label="trans('language')" v-if="organization.id == 198">
            <b-form-radio-group :options="localeOptions" v-model="locale" :disabled="busy" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group :label="trans('description')" v-if="organization.id == 198">
        <html-textarea :organization="organization" v-model="description" :disabled="busy"></html-textarea>
      </b-form-group>
    </div>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      {{ trans('please_wait') }}
    </p>
    <p v-if="error" class="text-danger">
      {{ error }}
    </p>

    <template #modal-footer>
      <b-row class="w-100">
        <b-col v-if="!!page && !page.is_built_in">
          <b-button variant="link" @click="onMovePage" :disabled="!canSave">
            {{ trans(page.is_staff ? 'move_page_to_client_portal' : 'move_page_to_staff_portal') }}
            <i class="fa fa-fw fa-arrow-right"></i>
          </b-button>
        </b-col>
        <b-col class="text-right">
          <b-button variant="light" @click="$bvModal.hide(id)">
            {{ trans('cancel') }}
          </b-button>
          <b-button variant="primary" class="ml-2" @click="onSave" :disabled="!canSave">
            {{ trans(page ? 'save' : 'create') }}
          </b-button>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import CreatePageFromSampleModal from './CreatePageFromSampleModal'
  import ModelTreeselect from './../../../common/components/ModelTreeselect'
  import Treeselect from '@riophae/vue-treeselect'
  import ListDropdown from './../list/ListDropdown'

  export default {
    props: {
      id: {
        type: String,
        default: 'page-modal',
      },
      organization: Object,
      clientTabs: Array, // list of all client tabs
      page: Object,
      after: Number, // Add after a page Id. null to add before the fisrt page.
      nested: Boolean, // When true, will be a child
      isStaff: {
        type: Boolean,
        default: false,
      },
      pages: Array,
    },
    components: {
      HtmlTextarea,
      CreatePageFromSampleModal,
      ModelTreeselect,
      Treeselect,
      ListDropdown
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        is_staff: this.isStaff,
        locale: null,
        localeOptions: [
          {text: trans('french'), value: 'fr'},
          {text: trans('english'), value: 'en'},
          {text: trans('form_language_none').replace(':lang', trans(this.organization.locale == 'fr' ? 'french' : 'english')), value: null},
        ],
        name: null,
        slug: null,
        permission: 'public',
        passcode: null,
        passcode_required: false,
        description: null,
        published: false,
        reload: false,
        has_external_url: false,
        external_url: null,
        hide_admin_permissions: [],
        hide_navigation_menu: false,
        allowed_client_tab_ids: [],
        membership_ids: [],
        force_locale: false,
        translated_page_id: null,
        help_slug: null,
        recipient_list_id: null,
      }
    },
    computed: {
      pagePermissionsOptions() {
        const basePermission = _.permissionOptions
        basePermission.push({
          value: 'list',
          text: trans('list')
        })
        return basePermission
      },
      canSave() {
        return !this.busy &&
          !!this.name &&
          (!this.passcode_required || this.passcode && this.passcode.trim())
      },
      parentPageId() {
        return this.nested ? this.after : (this.page ? this.page.page_id : null)
      },
      hideAdminPermissionOptions() {
        return [
          {id: 'staff', label: trans('permission_staff')},
          {id: 'staff_no_messaging', label: trans('permission_staff_no_messaging')},
          {id: 'attendant', label: trans('permission_attendant')},
          {id: 'communication_agent', label: trans('permission_communication_agent')},
          {id: 'guest', label: trans('permission_guest')},
        ]
      },
      allowedClientTabOptions() {
        return _.map(this.clientTabs, tab => {
          return {
            id: tab.id,
            label: tab.name
          }
        })
      },
      serialized() {
        return {
          name: this.name, // title
          type: 'blocks',
          is_staff: this.is_staff,
          locale: this.force_locale ? this.locale : null,
          slug: null, // Back-end will update it based on name
          permission: this.permission,
          passcode: this.passcode,
          passcode_required: this.passcode_required,
          after: this.after,
          page_id: this.parentPageId,
          description: this.description,
          published_to_partner: this.published && !!this.organization.partner ? this.organization.partner : null,
          external_url: this.has_external_url ? this.external_url : '',
          hide_admin_permissions: this.hide_admin_permissions,
          hide_navigation_menu: this.hide_navigation_menu,
          allowed_client_tab_ids: this.allowed_client_tab_ids,
          membership_ids: this.membership_ids,
          translated_page_id: this.translated_page_id,
          help_slug: this.help_slug,
          recipient_list_id: this.recipient_list_id,
        }
      },
      isUrlValid() {
        return _.validateUrl((this.external_url || '').trim())
      },
      translatedPages() {
        return _.reduce(this.pages, (pages, page) => {
          if (page.locale != this.locale) {
            pages.push({
              id: page.id,
              label: page.name,
            })
          }
          return pages
        }, [])
      },
    },
    methods: {
      onShown() {
        this.is_staff = this.page ? this.page.is_staff || false : this.isStaff
        this.locale = this.page ? this.page.locale : null
        this.name = this.page ? this.page.name : null
        this.slug = this.page ? this.page.slug : null
        this.permission = this.page ? this.page.permission : 'public'
        this.passcode = this.page ? this.page.passcode : null
        this.passcode_required = this.page ? this.page.passcode_required : false
        this.description = this.page ? this.page.description : null
        this.published = !!this.page && !!this.page.published_to_partner
        this.has_external_url = this.page && this.page.external_url ? true : false
        this.external_url = this.page ? this.page.external_url : null
        this.hide_admin_permissions = this.page ? this.page.hide_admin_permissions || [] : []
        this.hide_navigation_menu = this.page ? this.page.hide_navigation_menu : false
        this.allowed_client_tab_ids = this.page ? this.page.allowed_client_tab_ids || [] : []
        this.membership_ids = this.page ? this.page.membership_ids || [] : []
        this.translated_page_id = this.page ? this.page.translated_page_id : null
        this.help_slug = this.page ? this.page.help_slug : null
        if (this.page && this.page.locale) {
          this.force_locale = true
        }
        this.recipient_list_id = this.page ? this.page.recipient_list_id || null : null
      },
      onHidden() {
        this.busy = false
        this.error = null
        this.is_staff = this.isStaff
        this.locale = null
        this.name = null
        this.slug = null
        this.permission = 'public'
        this.passcode = null
        this.passcode_required = false
        this.description = null
        this.published = false
        this.external_url = null
        this.hide_admin_permissions = []
        this.hide_navigation_menu = false
        this.allowed_client_tab_ids = []
        this.membership_ids = []
        this.translated_page_id = null
        this.force_locale = false
        this.help_slug = null
        this.recipient_list_id = null,
        this.$emit('hidden')
      },
      onMovePage() {
        this.is_staff = !this.page.is_staff
        this.reload = true
        this.onSave()
      },
      onSave() {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/page`
        if (this.page) url += `/${this.page.id}`
        const method = this.page ? 'patch' : 'post'
        axios[method](url, this.serialized)
        .then(response => {
          if (this.reload) {
            window.location = response.data.url
            return
          }
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide(this.id)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window[_.camelCase(this.id)] = this
    },
  }
</script>
